import { useQuery } from "@tanstack/react-query";
import api from "@/lib/axiosConfig.ts";
import { usePuckEditorLocale } from "@/context/cms-puck/PuckEditorLocaleContext";
import { useCmsNew } from "@/lib/cms/cms-new-i18n";

type ShopArticle = {
  id: string;
  sellableTitle: string;
  priceAmount: number;
  previews?: { medium?: string }[];
};

/**
 * Aperçu éditeur du bloc `EshopListing` : mots-clés, nombre d'articles et
 * cartes des articles en vente, lus sur les mêmes routes publiques que le site
 * (`back/e-shop`, `back/e-shop/tags`). Tri, prix et favoris restent sur le site.
 * PARITÉ front : PuckEshopListing (ArticleList + CatalogueItem).
 */
export function EshopListingBlock() {
  const cmsNew = useCmsNew();
  const locale = usePuckEditorLocale() ?? "fr";
  const { data: shop } = useQuery({
    queryKey: ["cms-puck", "eshop-preview"],
    queryFn: async () =>
      (await api.get<{ articles: ShopArticle[] }>("/back/e-shop")).data,
  });
  const { data: tags = [] } = useQuery({
    queryKey: ["cms-puck", "eshop-preview-tags"],
    queryFn: async () => (await api.get<string[]>("/back/e-shop/tags")).data,
  });
  const articles = shop?.articles ?? [];

  return (
    <article className="flex flex-col pb-8 lg:grid lg:grid-cols-[minmax(20em,auto)_1fr]">
      <div className="hidden space-y-2 pl-8 pt-4 lg:block">
        <p className="font-medium">
          {cmsNew("cms.new.blocks.eshopListing.tags", "Filtrer par mots-clés")}
        </p>
        {tags.map((tag) => (
          <p key={tag} className="text-sm capitalize opacity-70">
            {tag}
          </p>
        ))}
      </div>
      <div className="flex flex-col gap-8 p-4">
        <p className="text-lg">
          {articles.length}{" "}
          {cmsNew("cms.new.blocks.eshopListing.count", "articles en vente")}
        </p>
        <ol className="flex w-full flex-col flex-wrap items-center gap-12 lg:flex-row lg:justify-start">
          {articles.map((article) => (
            <li key={article.id} className="flex flex-col items-center gap-4">
              <div className="relative h-60 w-80">
                {article.previews?.[0]?.medium ? (
                  <img
                    src={article.previews[0].medium}
                    alt=""
                    className="h-full w-full object-contain"
                  />
                ) : (
                  <div className="cms-page-surface h-full w-full" />
                )}
              </div>
              <div className="flex w-full flex-col gap-1">
                <h2 className="w-full text-left text-lg font-semibold">
                  {article.sellableTitle}
                </h2>
                <h3 className="w-full text-left text-lg font-normal">
                  {(article.priceAmount / 100).toLocaleString(locale, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}{" "}
                  €
                </h3>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </article>
  );
}
